/**
 * Opmaak in het callsheet (wiki-feedback Callsheets 26 sep 2026): Algemene opmerkingen en de
 * crew-briefing krijgen de opmaak-editor van Notities (TipTap, opslag als markdown).
 *
 * De editor schrijft markdown net iets anders weg dan mensen of de AI: harde regeleinden als
 * "\" + Enter, < als &lt;, en [ ] ontsnapt — waardoor bronverwijzingen als [notitie:…] niet
 * meer herkend worden. Deze helpers houden de opgeslagen tekst schoon en leesbaar, ook voor
 * Airtable, de PDF en de crew-app.
 */

const BRON_ONTSNAPT = /\\\[((?:mail|meeting|notitie|dossier|plak|onenote)(?::(?:[^\]\s\\]|\\.)+)?)\\\]/gi;

// Markdown-links: de editor heeft geen Link-extensie (en we voegen geen dependency toe), dus een
// link zou na bewerken alleen zijn label overhouden. Daarom gaat elke link vóór de editor naar
// een editor-veilig token: U+E000 label U+E001 ↗id U+E002. De volledige originele bron (mailto:,
// relatief pad, titel, <autolink>) staat in een register en komt na de editor terug. Het label
// (en "↗id") blijven zichtbaar en bewerkbaar; een verwijderd token = verwijderde link.
// Het id is een hash van de bron (7 base36) en het register is gedeeld per pagina: een token dat
// je naar een ander veld kopieert wijst naar dezelfde link. Onbekend id → alleen het label.
//
// Markeringen uit de Private Use Area (typt niemand; TipTap/tiptap-markdown laten ze staan).
// Opruimen gebeurt ALLEEN verankerd aan die tekens: gewone tekst met ⟦, ⟧, ↗ of id-achtige
// woorden blijft altijd ongemoeid (Codex-hercheck).
export const T_BEGIN = '';
export const T_ID = '';
export const T_EIND = '';
const ID_LEN = 7;
const PUA = '-';
const HEEL = new RegExp(`${T_BEGIN}([^${PUA}]*)${T_ID}↗?([0-9a-z]{0,7})${T_EIND}`, 'g');
const ZONDER_EIND = new RegExp(`${T_BEGIN}([^${PUA}]*)${T_ID}↗?([0-9a-z]{0,7})`, 'g');
const ZONDER_IDSCHEIDING = new RegExp(`${T_BEGIN}([^${PUA}]*)${T_EIND}`, 'g');
const ZONDER_BEGIN = new RegExp(`${T_ID}↗?[0-9a-z]{0,7}${T_EIND}?`, 'g');
const LOSSE_PUA = new RegExp(`[${PUA}]`, 'g');

const AUTOLINK = /^<((?:https?|mailto):[^\s<>]+)>/i;
const schoneUrl = (url) => url.replace(/\\([\\`*_{}[\]()#+\-.!~>|])/g, '$1');

/** Register: bron → id en id → link. */
export function nieuwLinkRegister() {
  return { perBron: new Map(), perId: new Map() };
}

/** Eén register voor de hele pagina (alle callsheet-editors), zodat kopiëren tussen velden werkt. */
export const paginaLinkRegister = nieuwLinkRegister();

/** Korte, stabiele hash van de bron (FNV-1a, base36, altijd 7 tekens). */
export function linkId(bron) {
  let h = 0x811c9dc5;
  for (let i = 0; i < bron.length; i += 1) {
    h ^= bron.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36).padStart(7, '0');
}

function registreer(reg, bron, link) {
  if (reg.perBron.has(bron)) return reg.perBron.get(bron);
  let id = linkId(bron);
  // Botsing (andere bron, zelfde hash): doortellen tot een vrij id — nooit een bestaande overschrijven.
  for (let n = 1; reg.perId.has(id) && reg.perId.get(id).sleutel !== bron; n += 1) id = linkId(`${bron}#${n}`);
  reg.perBron.set(bron, id);
  reg.perId.set(id, { ...link, sleutel: bron });
  return id;
}

/** Eind van "(bestemming "titel")" vanaf de "(" op positie k, of -1. */
function eindVanBestemming(md, k) {
  let i = k + 1;
  while (md[i] === ' ' || md[i] === '\t') i += 1;
  if (md[i] === '<') {
    while (i < md.length && md[i] !== '>' && md[i] !== '\n') i += 1;
    if (md[i] !== '>') return -1;
    i += 1;
  } else {
    let diepte = 0;
    while (i < md.length) {
      const c = md[i];
      if (c === '\\') { i += 2; continue; }
      if (/\s/.test(c)) break;
      if (c === '(') diepte += 1;
      if (c === ')') { if (diepte === 0) break; diepte -= 1; }
      i += 1;
    }
  }
  while (md[i] === ' ' || md[i] === '\t' || (md[i] === '\n' && md[i + 1] !== '\n')) i += 1;
  const open = md[i];
  const sluit = { '"': '"', "'": "'", '(': ')' }[open];
  if (sluit) {
    i += 1;
    while (i < md.length && md[i] !== sluit) { if (md[i] === '\\') i += 1; i += 1; }
    if (md[i] !== sluit) return -1;
    i += 1;
    while (md[i] === ' ' || md[i] === '\t') i += 1;
  }
  return md[i] === ')' ? i + 1 : -1;
}

/** Vervang alle markdown-links en <autolinks> door tokens (code-spans en afbeeldingen blijven). */
export function linksNaarTokens(md, reg) {
  const t = String(md || '');
  let uit = '';
  let i = 0;
  while (i < t.length) {
    const c = t[i];
    if (c === '\\') { uit += t.slice(i, i + 2); i += 2; continue; }
    if (c === '`') {
      const run = /^`+/.exec(t.slice(i))[0];
      const eind = t.indexOf(run, i + run.length);
      if (eind > -1) { uit += t.slice(i, eind + run.length); i = eind + run.length; continue; }
    }
    if (c === '<') {
      const m = AUTOLINK.exec(t.slice(i));
      if (m) {
        const id = registreer(reg, m[0], { auto: true, bron: m[0] });
        uit += `${T_BEGIN}${m[1]}${T_ID}↗${id}${T_EIND}`;
        i += m[0].length;
        continue;
      }
    }
    if (c === '[' && t[i - 1] !== '!') {
      let j = i + 1;
      let diepte = 1;
      while (j < t.length) {
        if (t[j] === '\\') { j += 2; continue; }
        if (t[j] === '\n' && t[j + 1] === '\n') break;
        if (t[j] === '[') diepte += 1;
        if (t[j] === ']') { diepte -= 1; if (diepte === 0) break; }
        j += 1;
      }
      const label = t.slice(i + 1, j);
      if (diepte === 0 && t[j + 1] === '(' && !/[\uE000-\uE002]/.test(label)) {
        const eind = eindVanBestemming(t, j + 1);
        if (eind > -1) {
          const bron = t.slice(i, eind);
          const id = registreer(reg, bron, { auto: false, bestemming: t.slice(j + 1, eind) });
          uit += `${T_BEGIN}${label}${T_ID}↗${id}${T_EIND}`;
          i = eind;
          continue;
        }
      }
    }
    uit += c;
    i += 1;
  }
  return uit;
}

/** Tokens terug naar links. Label uit het token (misschien bewerkt), bestemming uit het register.
 *  Halve tokens (een markering weggehaald) vallen terug op de link of alleen het label. */
export function tokensNaarLinks(md, reg) {
  const terug = (label, id) => {
    const link = id.length === ID_LEN ? reg?.perId.get(id) : null;
    if (!link) return label;
    if (link.auto) return link.bron;
    const l = label.trim();
    return l ? `[${l}]${link.bestemming}` : '';
  };
  // Id-scheiding weg: de staart "↗id" van het token (door ons gezet) terugvinden.
  const zonderScheiding = (inhoud) => {
    const pijl = inhoud.lastIndexOf('↗');
    if (pijl < 0) return inhoud;
    return terug(inhoud.slice(0, pijl), inhoud.slice(pijl + 1));
  };
  return String(md || '')
    .replace(HEEL, (_m, label, id) => terug(label, id))
    .replace(ZONDER_EIND, (_m, label, id) => terug(label, id))
    .replace(ZONDER_IDSCHEIDING, (_m, inhoud) => zonderScheiding(inhoud))
    .replace(ZONDER_BEGIN, '')
    .replace(LOSSE_PUA, '');
}

/** Wat de editor wegschrijft → wat we opslaan. */
export function uitEditor(md, reg = paginaLinkRegister) {
  const schoon = String(md || '')
    .replace(/(^|[^\\])\\\n/g, '$1\n')                               // harde regeleinde → gewone regel
    .replace(BRON_ONTSNAPT, (_m, ref) => `[${ref.replace(/\\(.)/g, '$1')}]`)
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/^(\s*[-*+]\s+)(\d+)\\\.(\s)/gm, '$1$2.$3')         // "- 01\. Film" → "- 01. Film" (naarEditor zet hem terug)
    .replace(/https?:\/\/[^\s<>()\uE000-\uE002]+/g, schoneUrl);                 // losse URL's: geen \_ of \*
  return tokensNaarLinks(schoon, reg).replace(/\s+$/, '');
}

/** Opgeslagen tekst → editor. Links worden tokens (zie boven). "- 01. Film" (deliverables)
 *  blijft een opsommingsteken met tekst; zonder ontsnapping maakt markdown er een genummerde
 *  lijst in een lijst van. */
export function naarEditor(md, reg = paginaLinkRegister) {
  return linksNaarTokens(md, reg).replace(/^(\s*[-*+•]\s+)(\d+)\.(\s)/gm, '$1$2\\.$3');
}

/** Voor react-markdown: Enter = nieuwe regel, net als in de editor en de PDF. */
export function metRegeleinden(md) {
  return String(md || '').replace(/([^\n])\n(?=[^\n])/g, '$1  \n');
}

/**
 * Leren van correcties: welke overgenomen AI-voorstellen gaan naar de server?
 * De server beslist of het echt een correctie is (opmaakruis telt niet) en laat een
 * oude correctie vervallen als de PL terugging naar het voorstel.
 * @param voorstellen  { veldKey: AI-voorstel }
 * @param opgeslagen   { veldKey: opgeslagen tekst }
 */
export function correctiesVoor(voorstellen, opgeslagen) {
  return Object.entries(opgeslagen || {})
    .filter(([k, v]) => typeof voorstellen?.[k] === 'string' && String(voorstellen[k]).trim() && typeof v === 'string')
    .map(([k, v]) => ({ veld: k, voorstel: voorstellen[k], opgeslagen: v }));
}
